import { AxiosError } from 'axios';
import { showErrorToast } from './toast';

/** Canonical copy for view-only / read-only acting profile. Use everywhere. */
export const APP_VIEW_ONLY_MESSAGE = 'You only have view access.';

let lastViewOnlyToastAt = 0;
let lastViewOnlyToastMessage = '';

/** Throttled toast so UI + API error handlers never stack duplicates. */
export function notifyViewOnlyAccess(message: string = APP_VIEW_ONLY_MESSAGE) {
  const text = message?.trim() || APP_VIEW_ONLY_MESSAGE;
  const now = Date.now();
  if (text === lastViewOnlyToastMessage && now - lastViewOnlyToastAt < 2500) {
    return;
  }
  lastViewOnlyToastAt = now;
  lastViewOnlyToastMessage = text;
  showErrorToast(text);
}

function normalizeAccessMessage(raw: unknown): string {
  return String(raw || '').toLowerCase();
}

export function isViewOnlyAccessMessage(raw: unknown): boolean {
  const msg = normalizeAccessMessage(raw);
  if (!msg) return false;
  return (
    msg.includes('you only have view access') ||
    msg.includes('view-only') ||
    msg.includes('view only') ||
    msg.includes('read-only access') ||
    msg.includes('read only access') ||
    (msg.includes('permission denied') && msg.includes('view'))
  );
}

/**
 * True when the error represents "view-only / write blocked" in the active profile context.
 *
 * Covers:
 * - Client-side preflight blocking in `api/client.ts` (AxiosError code ERR_FORBIDDEN, no response)
 * - Backend 403 responses
 */
export function isWriteAccessBlockedError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  if (error.code === 'ERR_FORBIDDEN') return true;
  if (isViewOnlyAccessMessage(error.message)) return true;
  if (error.response?.status === 403) {
    const data = error.response.data as
      | { message?: string; error?: string }
      | undefined;
    if (
      isViewOnlyAccessMessage(data?.message) ||
      isViewOnlyAccessMessage(data?.error)
    ) {
      return true;
    }
  }
  return false;
}

/** Prefer canonical message when surfacing view-only API errors in UI. */
export function resolveWriteAccessErrorMessage(
  error: unknown,
  fallback?: string,
): string {
  if (isWriteAccessBlockedError(error)) return APP_VIEW_ONLY_MESSAGE;
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { message?: string; error?: string }
      | undefined;
    return (
      data?.message ||
      data?.error ||
      error.message ||
      fallback ||
      'Request failed'
    );
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback || 'Request failed';
}
